// =============================================================================
// features/sales/campaigns/wizard/steps/Step6Review.tsx — Etapa 6: resumo,
// riscos e confirmação explícita (F35-S08, doc 26 §6.6). "Salvar" NUNCA
// ativa — é só navegar de volta com o rascunho já persistido a cada etapa
// anterior. "Ativar campanha" é a única ação que muda o status, e exige a
// confirmação de impacto de `ActivateConfirmDialog`.
// =============================================================================

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../../../components/ui/Button';
import { useToast } from '../../../../../components/ui/Toast';
import { useAssignableAgents } from '../../../../../hooks/admin/useAgents';
import {
  useActivateCampaign,
  useMembersSummary,
} from '../../../../../hooks/sales/useCampaignWizard';
import { ApiError } from '../../../../../lib/api';
import { WEEKDAY_LABEL, WEEKDAY_ORDER } from '../cadence';
import { ActivateConfirmDialog } from '../components/ActivateConfirmDialog';
import { WizardStepShell } from '../components/WizardStepShell';
import { ASSIGNMENT_STRATEGY_OPTIONS } from '../labels';
import type {
  CadenceValues,
  DistributionValues,
  HygieneResultState,
  ObjectiveValues,
} from '../types';

export interface Step6ReviewProps {
  campaignId: string;
  objective: ObjectiveValues;
  hygiene: HygieneResultState | null;
  distribution: DistributionValues;
  cadence: CadenceValues;
  onBack: () => void;
}

export function Step6Review({
  campaignId,
  objective,
  hygiene,
  distribution,
  cadence,
}: Step6ReviewProps): React.JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: membersSummary } = useMembersSummary(campaignId);
  const { agents } = useAssignableAgents();
  const { mutateAsync: activate } = useActivateCampaign();
  const [showConfirm, setShowConfirm] = React.useState(false);

  const strategy = ASSIGNMENT_STRATEGY_OPTIONS.find(
    (o) => o.value === distribution.assignmentStrategy,
  );
  const activeDays = WEEKDAY_ORDER.filter((d) => cadence.weeklyWindow[d] !== undefined);

  const risks: string[] = [];
  if (activeDays.length === 0)
    risks.push('Nenhuma janela semanal configurada — a ativação será recusada.');
  if ((membersSummary?.total ?? 0) === 0) {
    risks.push('Nenhum cliente na carteira ainda — aplique o público na etapa de higiene.');
  }
  if (hygiene && hygiene.counts.blocked > 0) {
    risks.push(
      `${hygiene.counts.blocked} cliente(s) entram como bloqueados — nunca ficam disponíveis para a fila.`,
    );
  }

  async function handleActivate(): Promise<{ ok: true } | { ok: false; message: string }> {
    try {
      await activate(campaignId);
      toast('Campanha ativada.', 'success');
      navigate('/campanhas');
      return { ok: true };
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível ativar a campanha agora.';
      return { ok: false, message };
    }
  }

  return (
    <WizardStepShell
      title="Revisão"
      description="Confira tudo antes de ativar — salvar não coloca a campanha na fila de ninguém."
      onContinue={() => navigate('/campanhas')}
      continueLabel="Salvar e sair"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SummaryBlock title="Objetivo">
          <SummaryRow label="Nome" value={objective.name} />
          <SummaryRow label="Prioridade" value={String(objective.priority)} />
        </SummaryBlock>

        <SummaryBlock title="Público">
          <SummaryRow
            label="Na carteira"
            value={
              membersSummary
                ? `${membersSummary.total.toLocaleString('pt-BR')} (${membersSummary.blocked.toLocaleString('pt-BR')} bloqueado(s))`
                : '—'
            }
          />
          <SummaryRow
            label="Última aplicação"
            value={hygiene ? `${hygiene.willMaterialize} adicionados` : 'Ainda não aplicado'}
          />
        </SummaryBlock>

        <SummaryBlock title="Distribuição">
          <SummaryRow label="Estratégia" value={strategy?.label ?? '—'} />
          <SummaryRow label="Vendedores habilitados" value={String(agents.length)} />
        </SummaryBlock>

        <SummaryBlock title="Cadência">
          <SummaryRow
            label="Dias ativos"
            value={
              activeDays.length > 0 ? activeDays.map((d) => WEEKDAY_LABEL[d]).join(', ') : 'Nenhum'
            }
          />
          <SummaryRow
            label="Tentativas / intervalo"
            value={`${cadence.maxAttempts} / ${cadence.minIntervalMinutes} min`}
          />
        </SummaryBlock>
      </div>

      {risks.length > 0 && (
        <div
          role="alert"
          className="rounded-sm px-4 py-3 border-l-[3px] flex flex-col gap-1"
          style={{ borderColor: 'var(--warning)', background: 'var(--warning-bg)' }}
        >
          <p className="font-sans text-sm font-semibold text-ink">Antes de ativar, veja isso:</p>
          <ul className="list-disc pl-5">
            {risks.map((risk) => (
              <li key={risk} className="font-sans text-xs text-ink-2">
                {risk}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="button" variant="secondary" onClick={() => setShowConfirm(true)}>
          Ativar campanha
        </Button>
      </div>

      {showConfirm && (
        <ActivateConfirmDialog
          campaignName={objective.name}
          strategyLabel={strategy?.label ?? distribution.assignmentStrategy}
          membersTotal={membersSummary?.total ?? 0}
          membersBlocked={membersSummary?.blocked ?? 0}
          vendorPoolSize={agents.length}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleActivate}
        />
      )}
    </WizardStepShell>
  );
}

function SummaryBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className="rounded-sm border border-border p-4 flex flex-col gap-1.5"
      style={{ background: 'var(--surface-1)' }}
    >
      <p className="font-sans text-xs font-bold uppercase tracking-[0.06em] text-ink-3">{title}</p>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <p className="font-sans text-sm text-ink">
      <span className="text-ink-3">{label}: </span>
      <span className="font-medium">{value}</span>
    </p>
  );
}
